// api/apiClient.js

import { supabase } from "../supabaseClient";
import { getToken } from "../Helper/auth";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

/**
 * Core request handler
 */
const request = async (endpoint, method, body = null) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 120000); // 2 min

  try {
    // 🔥 Always get fresh token from Supabase
    const token = await getToken();

    const res = await fetch(`${BASE_URL}${endpoint}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeout);

    // ✅ handle empty responses safely
    const text = await res.text();
    const data = text ? JSON.parse(text) : {};

    // ❌ Unauthorized → logout & redirect
    if (res.status === 401) {
      console.warn("401 Unauthorized – signing out user");
      await supabase.auth.signOut();
      window.location.href = "/login";
      return;
    }

    if (!res.ok) {
      throw new Error(
        data.message ||
          data.detail ||
          `API Error (${res.status})`
      );
    }

    return data;
  } catch (err) {
    if (err.name === "AbortError") {
      throw new Error("⏰ Request timed out after 120 seconds.");
    }
    throw err;
  }
};

/**
 * Reusable API client
 */
export const apiClient = {
  get: (endpoint) => request(endpoint, "GET"),
  post: (endpoint, body) => request(endpoint, "POST", body),
  put: (endpoint, body) => request(endpoint, "PUT", body),
  delete: (endpoint) => request(endpoint, "DELETE"),
};
